import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Minus, Plus, Truck, RotateCcw, X, Ruler } from "lucide-react";
import { toast } from "sonner";
import api, { inr } from "../lib/api";
import { optImg } from "../lib/assets";
import { useShop } from "../context/ShopContext";
import ProductCard from "../components/ProductCard";
import SizeFinder, { chartKeyFor, getFitRecs } from "../components/SizeFinder";
import { Reveal } from "../components/Reveal";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addToCart, toggleWishlist, wishlist, setCartOpen } = useShop();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [img, setImg] = useState(0);
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [guideOpen, setGuideOpen] = useState(false);
  const [fitOpen, setFitOpen] = useState(false);
  const [chart, setChart] = useState(null);
  const [fitRec, setFitRec] = useState(null);
  const [pin, setPin] = useState("");
  const [pinResult, setPinResult] = useState("");
  const [tab, setTab] = useState("details");

  useEffect(() => {
    setLoading(true);
    setImg(0); setSize(""); setQty(1);
    api.get(`/products/${slug}`)
      .then((r) => { setProduct(r.data); setColor(r.data.colors?.[0]?.name || ""); document.title = `${r.data.name} — Velour & Vane`; })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setGuideOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!product) return;
    const key = chartKeyFor(product);
    api.get("/size-charts").then((r) => setChart(r.data.find((c) => c.key === key) || null)).catch(() => {});
    const recs = getFitRecs();
    const rec = recs[key];
    if (rec && (product.sizes || []).includes(rec.size)) {
      setFitRec(rec);
      setSize(rec.size);
    } else {
      setFitRec(null);
    }
  }, [product]);

  if (loading) {
    return <main className="min-h-screen flex items-center justify-center"><p className="font-display text-2xl text-vv-stone animate-pulse">Velour &amp; Vane</p></main>;
  }
  if (!product) {
    return (
      <main data-testid="product-not-found" className="min-h-[60vh] flex flex-col items-center justify-center gap-6">
        <p className="font-display text-3xl">This piece has moved on.</p>
        <Link to="/shop" className="vv-btn-dark">Back to Shop</Link>
      </main>
    );
  }

  const wished = wishlist.some((p) => p.id === product.id);
  const sizeStock = product.size_stock || {};
  const isSoldOut = (s) => (s in sizeStock ? (sizeStock[s] ?? 0) <= 0 : (product.stock ?? 0) <= 0);
  const allSoldOut = (product.sizes || []).every((s) => isSoldOut(s));

  const handleAdd = (buyNow = false) => {
    if (!size && product.sizes?.length > 1) {
      toast.error("Please select a size");
      return;
    }
    addToCart(product, size || product.sizes?.[0] || "One Size", color, qty);
    if (buyNow) window.location.href = "/checkout";
    else setCartOpen(true);
  };

  const checkPin = async () => {
    if (!/^[1-9]\d{5}$/.test(pin)) { setPinResult("Enter a valid 6-digit PIN code."); return; }
    try {
      const { data } = await api.get(`/pincode/${pin}`);
      if (!data.valid) setPinResult(data.message);
      else setPinResult(`Delivering to ${data.district}, ${data.state} in 3–6 working days. COD available.`);
    } catch {
      setPinResult("Could not verify this PIN right now. Try again.");
    }
  };

  return (
    <main data-testid="product-page" className="px-4 sm:px-8 lg:px-16 max-w-[1600px] mx-auto py-8 md:py-14 pb-28 md:pb-14">
      <p className="vv-overline text-vv-muted mb-8">
        <Link to="/" className="hover:text-vv-black">Home</Link> / <Link to={`/shop?category=${product.category}`} className="hover:text-vv-black capitalize">{product.category.replace("-", " ")}</Link> / {product.name}
      </p>
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-20">
        <div>
          <Reveal y={20}>
            <div data-testid="product-gallery" className="relative overflow-hidden bg-vv-elevated aspect-[3/4]">
              <img src={optImg(product.images?.[img], 1000)} alt={product.name} decoding="async" className="w-full h-full object-cover" />
              {product.tags?.includes("new") && (
                <span className="absolute top-4 left-4 text-[9px] tracking-[0.25em] px-2.5 py-1 bg-vv-forest text-vv-ivory">NEW</span>
              )}
            </div>
          </Reveal>
          {product.images?.length > 1 && (
            <div className="flex gap-3 mt-3">
              {product.images.map((src, i) => (
                <button key={i} data-testid={`gallery-thumb-${i}`} onClick={() => setImg(i)}
                  className={`w-20 aspect-[3/4] overflow-hidden bg-vv-elevated border ${img === i ? "border-vv-black" : "border-transparent"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:pt-4">
          <h1 data-testid="product-name" className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">{product.name}</h1>
          <p data-testid="product-price" className="text-xl mt-4 font-medium">{inr(product.price)}</p>
          <p className="text-[11px] text-vv-muted mt-1">Inclusive of all taxes</p>

          {product.colors?.length > 0 && (
            <div className="mt-8">
              <p className="vv-overline mb-4">Colour — <span className="text-vv-muted normal-case tracking-normal">{color}</span></p>
              <div className="flex gap-3">
                {product.colors.map((c) => (
                  <button key={c.name} data-testid={`color-select-${c.name.toLowerCase().replace(/\s/g, "-")}`} title={c.name}
                    onClick={() => setColor(c.name)}
                    className={`w-9 h-9 rounded-full border ${color === c.name ? "ring-2 ring-offset-2 ring-vv-black ring-offset-vv-ivory" : "border-vv-black/20"}`}
                    style={{ background: c.hex }} />
                ))}
              </div>
            </div>
          )}

          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <p className="vv-overline">Size</p>
              <div className="flex items-center gap-5">
                <button data-testid="find-fit-button" onClick={() => setFitOpen(true)}
                  className="text-[10px] uppercase tracking-[0.2em] font-medium vv-link-underline inline-flex items-center gap-1.5">
                  <Ruler size={12} strokeWidth={1.5} /> Find Your Perfect Fit
                </button>
                <button data-testid="size-guide-button" onClick={() => setGuideOpen(true)}
                  className="text-[10px] uppercase tracking-[0.2em] text-vv-muted vv-link-underline">Size Guide</button>
              </div>
            </div>
            {fitRec && (
              <p data-testid="fit-recommendation-note" className="text-[11px] text-vv-forest mb-3">
                Our recommendation for you: <span className="font-medium">size {fitRec.size}</span> — {fitRec.fit_preference} fit
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {(product.sizes || []).map((s) => {
                const out = isSoldOut(s);
                const isRec = fitRec?.size === s;
                return (
                  <button key={s} data-testid={`size-select-${s.toLowerCase().replace(/\s/g, "-")}`} onClick={() => !out && setSize(s)}
                    disabled={out} title={out ? "Sold out" : s}
                    className={`relative min-w-[3rem] px-3 py-2.5 text-xs tracking-wider border transition-colors ${
                      out ? "border-vv-black/10 text-vv-stone/50 line-through cursor-not-allowed"
                        : size === s ? "bg-vv-black text-vv-ivory border-vv-black" : "border-vv-black/20 hover:border-vv-black"}`}>
                    {s}
                    {isRec && !out && <span data-testid="fit-rec-badge" className="absolute -top-2 -right-2 bg-vv-forest text-vv-ivory text-[7px] tracking-widest uppercase px-1.5 py-0.5">Fit</span>}
                  </button>
                );
              })}
            </div>
            {allSoldOut && <p data-testid="sold-out-note" className="text-[11px] text-vv-burgundy mt-3">This piece is currently sold out.</p>}
            {product.fit && <p className="text-[11px] text-vv-muted mt-3">{product.fit}</p>}
            {chart?.model && (
              <p data-testid="model-info" className="text-[11px] text-vv-muted mt-2">
                Model is {chart.model.height} cm{chart.model.weight ? `, ${chart.model.weight} kg` : ""} and wears size {chart.model.wears}.
              </p>
            )}
          </div>

          <div className="flex items-stretch gap-3 mt-10">
            <div className="flex items-center border border-vv-black/20">
              <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="px-3" aria-label="Decrease quantity"><Minus size={14} /></button>
              <span data-testid="qty-value" className="w-8 text-center text-sm">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty(qty + 1)} className="px-3" aria-label="Increase quantity"><Plus size={14} /></button>
            </div>
            <button data-testid="add-to-cart-button" onClick={() => handleAdd(false)} className="vv-btn-dark flex-1">Add to Bag</button>
            <button data-testid="pdp-wishlist-button" onClick={() => toggleWishlist(product)} aria-label="Wishlist"
              className="w-12 border border-vv-black/20 flex items-center justify-center hover:border-vv-black transition-colors">
              <Heart size={16} strokeWidth={1.5} className={wished ? "fill-vv-burgundy text-vv-burgundy" : ""} />
            </button>
          </div>
          <button data-testid="buy-now-button" onClick={() => handleAdd(true)} className="vv-btn-outline-dark w-full mt-3">Buy Now</button>

          <div className="mt-8 border border-vv-black/10 p-4 flex gap-3">
            <input data-testid="pincode-input" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Delivery PIN code"
              maxLength={6} className="flex-1 bg-transparent outline-none text-sm placeholder:text-vv-stone" />
            <button data-testid="pincode-check-button" onClick={checkPin} className="text-[10px] uppercase tracking-[0.2em] font-medium">Check</button>
          </div>
          {pinResult && <p data-testid="pincode-result" className="text-[11px] text-vv-forest mt-2">{pinResult}</p>}

          <div className="mt-10 border-t border-vv-black/10">
            {[["details", "Details & Fabric"], ["shipping", "Shipping & Returns"]].map(([key, label]) => (
              <div key={key} className="border-b border-vv-black/10">
                <button data-testid={`tab-${key}`} onClick={() => setTab(tab === key ? "" : key)}
                  className="w-full flex justify-between items-center py-5 text-left">
                  <span className="vv-overline">{label}</span>
                  <span className="text-lg font-light">{tab === key ? "−" : "+"}</span>
                </button>
                {tab === key && (
                  <div className="pb-6 text-sm text-vv-muted leading-relaxed space-y-2">
                    {key === "details" ? (
                      <>
                        <p data-testid="product-description">{product.description}</p>
                        {product.fabric && <p className="font-mono text-[11px] pt-2">FABRIC — {product.fabric}</p>}
                        {product.gsm && <p className="font-mono text-[11px]" data-testid="product-gsm">WEIGHT — {product.gsm} GSM</p>}
                        <p className="font-mono text-[11px]">CARE — Machine wash cold, inside out. Line dry in shade.</p>
                        {chart?.model && <p className="font-mono text-[11px]">MODEL — {chart.model.height} cm, wears size {chart.model.wears}</p>}
                      </>
                    ) : (
                      <>
                        <p className="flex items-center gap-2"><Truck size={14} strokeWidth={1.5} /> Free shipping on orders above {inr(1999)}. Dispatched in 24–48 hours.</p>
                        <p className="flex items-center gap-2"><RotateCcw size={14} strokeWidth={1.5} /> Easy 7-day returns and exchanges across India.</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {product.related?.length > 0 && (
        <section data-testid="related-products" className="mt-24 md:mt-32">
          <Reveal>
            <p className="vv-overline text-vv-muted mb-4">Complete the Look</p>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-12">YOU MAY ALSO LIKE</h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-8">
            {product.related.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}><ProductCard product={p} /></Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Mobile sticky bar */}
      <div data-testid="mobile-sticky-bar" className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-vv-ivory border-t border-vv-black/10 px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs truncate">{product.name}</p>
          <p className="text-sm font-medium">{inr(product.price)}</p>
        </div>
        <button data-testid="sticky-add-to-cart" onClick={() => handleAdd(false)} className="vv-btn-dark !px-6 !py-3">Add to Bag</button>
      </div>

      {guideOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-vv-black/40" onClick={() => setGuideOpen(false)} />
          <div data-testid="size-guide-modal" className="relative bg-vv-ivory max-w-md w-full p-8 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <p className="font-display text-2xl">Size Guide — {chart?.label || "Garment"}</p>
              <button data-testid="size-guide-close" onClick={() => setGuideOpen(false)} aria-label="Close"><X size={20} strokeWidth={1.25} /></button>
            </div>
            {chart ? (
              <>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-vv-muted border-b border-vv-black/10">
                      <th className="py-2">Size</th>
                      {(chart.kind === "top" ? ["chest", "shoulder", "length"] : ["waist", "hip", "inseam"]).map((k) => (
                        <th key={k} className="capitalize">{k} (cm)</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {chart.sizes.map((r) => (
                      <tr key={r.size} className={`border-b border-vv-black/5 ${fitRec?.size === r.size ? "bg-vv-forest/5" : ""}`}>
                        <td className="py-2.5 font-medium">{r.size}{fitRec?.size === r.size && <span className="text-[8px] uppercase tracking-widest text-vv-forest ml-2">You</span>}</td>
                        {(chart.kind === "top" ? ["chest", "shoulder", "length"] : ["waist", "hip", "inseam"]).map((k) => (
                          <td key={k}>{r[k]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[11px] text-vv-muted mt-4">Garment measurements in cm. Our tees are cut oversized — take your usual size for the intended drape.</p>
              </>
            ) : (
              <p className="text-sm text-vv-muted">Size chart loading…</p>
            )}
          </div>
        </div>
      )}

      <SizeFinder
        product={product}
        open={fitOpen}
        onClose={() => setFitOpen(false)}
        onAddToCart={(recSize) => {
          const finalSize = recSize && (product.sizes || []).includes(recSize) && !isSoldOut(recSize)
            ? recSize : (size || product.sizes?.[0] || "One Size");
          setSize(finalSize);
          addToCart(product, finalSize, color, qty);
          setCartOpen(true);
          setFitOpen(false);
          toast.success(`Added size ${finalSize} to your bag`);
        }}
      />
    </main>
  );
}
